import {
  type Except,
  isEmpty,
  notEmpty,
  type ValidatedKey,
} from '@festina-life/core'
import { useField } from 'formik'
import type { ComponentProps, ReactNode } from 'react'

import { MultiSelect } from '../../forms/multiselect/MultiSelect'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import { withFormikInputDiffing } from '../formikDateInput/withFormikInputDiffing'
import {
  handleDefaultValueClick,
  handleExistingValueClick,
  handleOnBlur,
} from '../formikUtils/formikUtils'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import { useSimpleValueDifferenceCheck } from '../formikUtils/useSimpleValueDifferenceCheck'
import type { FormikInputPropsForWrapper } from '../types'

type Props<T> = Except<
  ComponentProps<typeof MultiSelect<T>>,
  'onChange' | 'values' | 'onBlur' | 'wrapperProps'
> & {
  name: ValidatedKey
  defaultValue?: T[]
  value?: T[]
  onBlur?: (value: T[] | undefined) => void
} & Except<FormikInputPropsForWrapper, 'validationText'>

const FormikMultiSelectInternal = <T,>({
  name,
  options,
  description,
  defaultValue,
  onDefaultValueClick,
  onBlur,
  label,
  isRequired,
  disabled,
  hasRightColumnAlways,
  className,
  existingValueConfig,
  isTableCell,
  ...restProps
}: Props<T>) => {
  const [field, meta, { setValue }] = useField(name)
  const { isInError, error } = useFormikErrorWithState(meta)
  const isFieldDisabled = useFormikIsDisabled(disabled)
  const existingValue = useExistingValuesContext()?.getExistingValue?.(name) as
    | []
    | undefined

  const toRenderString = (
    value: T[] | undefined,
    renderFn: ((item: T) => ReactNode) | undefined,
  ) => {
    if (isEmpty(value)) {
      return value
    }

    return renderFn
      ? existingValue
          ?.map((existingValue) => renderFn(existingValue))
          .join(', ')
      : value.join(', ')
  }

  const handleOnChange = async (value: T[]) => {
    await setValue(value)
    onBlur?.(value)
  }

  const inputValueDifferenceFromExistingValue =
    useSimpleValueDifferenceCheck(name)

  const wrapperProps = getWrapperProps({
    isTableCell,
    label,
    existingValue: existingValue
      ? toRenderString(existingValue, restProps.renderFn)
      : undefined,
    existingValueConfig,
    inputValueDifferenceFromExistingValue,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      description,
      defaultValue:
        restProps.renderFn && notEmpty(defaultValue) ? (
          <div className="flex gap-2">
            {defaultValue.map(restProps.renderFn)}
          </div>
        ) : undefined,
      onDefaultValueClick: () =>
        handleDefaultValueClick({
          defaultValue,
          onBlur,
          onDefaultValueClick,
        }),
      onExistingValueClick: () =>
        handleExistingValueClick({
          existingValue,
          setValue,
          onBlur,
        }),
      htmlFor: name,
      error: error ? meta.error : undefined,
      hasRightColumnAlways,
      className,
    },
  })

  return (
    <MultiSelect
      wrapperProps={wrapperProps}
      options={options}
      values={field.value}
      isInError={isInError}
      disabled={isFieldDisabled}
      {...field}
      {...restProps}
      onChange={(_, value) => handleOnChange(value)}
      onBlur={(event) => handleOnBlur({ event, field })}
      onBlurValue={(value) => {
        onBlur?.(value)
      }}
    />
  )
}

export const FormikMultiSelect = withFormikInputDiffing(
  FormikMultiSelectInternal,
)
